/* eslint-disable no-unused-vars */
import axios from 'axios';
import { createContext, useState } from 'react';
import { loginService, registerService, getUserService, verifyAccountService, logoutService } from '@/api/UserApi';
import useAuthStore from '@/store/authStore';
import { toast } from 'react-toastify';

// URL của API cũ từ biến môi trường
const API_URL = import.meta.env.VITE_APP_OLD_API_URL;
// URL cho quên mật khẩu
export const FORGOT_PASSWORD_URL = `${API_URL}/forgot-password`;
// URL cho đặt lại mật khẩu
export const RESET_PASSWORD_URL = `${API_URL}/reset-password`;

// Tạo AuthContext
const AuthContext = createContext(null);

// AuthProvider component: cung cấp AuthContext cho các component con
const AuthProvider = ({
  children
}) => {
  // State loading: chỉ thị trạng thái đang tải dữ liệu hay không
  const [loading, setLoading] = useState(true);
  // State auth: lưu trữ token xác thực từ localStorage
  const [auth, setAuth] = useState(localStorage.getItem('token'));
  // State currentUser: lưu trữ thông tin của người dùng hiện tại đã đăng nhập
  const [currentUser, setCurrentUser] = useState();

  // Lấy actions từ Zustand store
  const { setToken: setStoreToken, setUser: setStoreUser, login: storeLogin, logout: storeLogout } = useAuthStore();

  // Hàm verify: kiểm tra tính hợp lệ của token và lấy thông tin user khi load ứng dụng
  const verify = async () => {
    if (auth) {
      try {
        const user = await getUser(); // Lấy thông tin user
        setCurrentUser(user); // Lưu thông tin user vào state
        setStoreUser(user); // Lưu thông tin user vào store
      } catch {
        // Nếu token không hợp lệ hoặc có lỗi, xóa token và thông tin user
        saveAuth(undefined);
        setCurrentUser(undefined);
        setStoreToken(null); // Xóa token trong store
        setStoreUser(null); // Xóa user trong store
      }
    }
  };

  // Hàm saveAuth: lưu/xóa token trong state và localStorage
  const saveAuth = auth => {
    setAuth(auth);
    setStoreToken(auth); // Lưu token vào store
    if (auth) {
      localStorage.setItem('token', auth);
    } else {
      localStorage.removeItem('token');
    }
  };

  // Hàm login: xử lý quá trình đăng nhập
  const login = async (email, password) => {
    try {
      // Gọi API đăng nhập
      const response = await loginService({ email, password });

      // Kiểm tra nếu phản hồi có access_token và user
      if (response?.access_token && response?.user) {
        saveAuth(response.access_token); // Lưu token
        setCurrentUser(response.user); // Lưu thông tin user
        storeLogin(response.access_token, response.user); // Lưu vào store
        return true; // Trả về true nếu đăng nhập thành công
      } else {
        // Nếu phản hồi không đúng cấu trúc, log lỗi và ném exception
        console.error("Login successful response missing access_token or user:", response);
        throw new Error('Invalid response structure from server.');
      }
    } catch (error) {
      // Xử lý lỗi đăng nhập
      console.error('Login error:', error);
      throw error;
    }
  };

  // Hàm register: xử lý quá trình đăng ký
  const register = async (fullname, email, password, password_confirmation, cf_turnstile_response) => {
    try {
      // Gọi API đăng ký
      const response = await registerService({ 
        fullname,
        email, 
        password,
        password_confirmation,
        cf_turnstile_response
      });
      // Thành công
      if (response?.code === 'success' || response?.message === 'User created!') {
        return response?.data;
      }
      // Không thành công nhưng không ném lỗi (hiếm)
      throw new Error(response?.message || 'User creation failed.');
    } catch (error) {
      // Trả lỗi gốc để component xử lý song ngữ theo status
      throw error;
    }
  };

  // Hàm requestPasswordResetLink: yêu cầu gửi link đặt lại mật khẩu qua email
  const requestPasswordResetLink = async email => {
    await axios.post(FORGOT_PASSWORD_URL, {
      email
    });
  };

  // Hàm changePassword: đặt lại mật khẩu bằng token và email
  const changePassword = async (email, token, password, password_confirmation) => {
    await axios.post(RESET_PASSWORD_URL, {
      email,
      token,
      password,
      password_confirmation
    });
  };

  // Hàm getUser: gọi API để lấy thông tin user hiện tại
  const getUser = async () => {
    return await getUserService();
  };

  // Hàm logout: xử lý đăng xuất (gọi API logout và xóa token, thông tin user)
  const logout = async () => {
    try {
      // Gọi API logout để thông báo cho server
      await logoutService();
    } catch (error) {
      // Log lỗi nhưng vẫn tiếp tục logout local
      console.error('Logout API error:', error);
    } finally {
      // Luôn xóa thông tin local dù API có lỗi hay không
      saveAuth(undefined);
      setCurrentUser(undefined);
      storeLogout(); // Xóa thông tin trong store
    }
  };

  // Hàm verifyAccount: xử lý xác thực tài khoản bằng token
  const verifyAccount = async (token) => {
    try {
      // Gọi API xác thực tài khoản
      const response = await verifyAccountService(token);
      // Trả về message và status thành công
      return {
        message: response,
        status: 200
      };
    } catch (error) {
      // Xử lý lỗi xác thực tài khoản
      if (error.response) {
        // Trả về message lỗi và status từ phản hồi API
        return {
          message: error.response.data.error || error.response.data.message || 'Kích hoạt thất bại',
          status: error.response.status
        };
      }
      // Trả về message lỗi chung nếu không có phản hồi từ API
      return {
        message: error.message || 'Kích hoạt thất bại',
        status: 500
      };
    }
  };

  // Cung cấp các giá trị và hàm qua AuthContext.Provider
  return <AuthContext.Provider value={{
    loading, // Trạng thái tải
    setLoading, // Hàm set trạng thái tải
    auth, // Token xác thực
    saveAuth, // Hàm lưu/xóa token
    currentUser, // Thông tin người dùng hiện tại
    setCurrentUser, // Hàm set thông tin người dùng
    login, // Hàm đăng nhập
    register, // Hàm đăng ký
    requestPasswordResetLink, // Hàm yêu cầu đặt lại mật khẩu
    changePassword, // Hàm thay đổi mật khẩu
    getUser, // Hàm lấy thông tin user
    logout, // Hàm đăng xuất
    verify, // Hàm xác thực token khi load ứng dụng
    verifyAccount // Hàm xác thực tài khoản
  }}>
      {children} {/* Render các component con */}
    </AuthContext.Provider>;
};

// Export AuthContext và AuthProvider
export { AuthContext, AuthProvider };